import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import { EDITOR_WRITE_TOOL_NAMES } from '@flintmint/core-shared/utils/editor-write-tool';

import { getCachedEditor } from '@/hooks/use-block-editor/editor-cache';
import { isAiToolkitReady } from '@/utils/editor-utils';
import { getTargetedEditRange } from '../../edit-target';
import { recordFirstContent } from '../../note-generation-session-store';
import { executeReplaceSelectionTool } from '../replace-selection-tool';
import {
  getAgentRunEditScope,
  getAgentRunEditTarget,
  getAgentRunStreamingEnabled,
  getToolExecutionIgnoreReason,
  registerAgentRunStateClearer,
} from '../../run/agent-run-store';

export type StreamingToolEvent = {
  runId: number;
  toolCallId: string;
  toolName: string;
  input: unknown;
  isFinal: boolean;
};

type StreamableEditor = Parameters<typeof getAiToolkit>[0] & { isDestroyed?: boolean };

// Shared ~30fps cap for streamed editor previews (the coalescing below) and chat
// re-renders (used as `experimental_throttle` in use-llm-agent.ts). Streaming is
// choppy token-by-token anyway, so 30fps keeps per-frame work low on weak machines.
export const STREAMING_UI_FRAME_MS = 33;

type StreamingSession = {
  active: boolean;
  runId: number | null;
  lastAppliedKey: string | null;
  pendingEvent: StreamingToolEvent | null;
  rafHandle: number | null;
  lastAppliedAt: number;
};

const streamingSessions = new Map<string, StreamingSession>();

// Streaming preview is per-run side state that lives outside the run store (the store
// cannot import this module without a cycle). Register the clear-all hook so
// `clearAllAgentRunState()` still tears down streaming sessions. Registration is lazy —
// done on first session creation rather than at import time — to avoid touching the
// store before it finishes initializing under circular imports. Streaming sessions only
// exist after `beginStreamingSession`, so the clearer is always registered before there
// is anything to clear.
let clearerRegistered = false;

function ensureStreamingClearerRegistered(): void {
  if (clearerRegistered) return;
  clearerRegistered = true;
  registerAgentRunStateClearer(clearAllStreamingSessions);
}

export function beginStreamingSession(requestId: string, runId: number): void {
  ensureStreamingClearerRegistered();
  const session = getOrCreateStreamingSession(requestId);

  cancelPendingFrame(session);
  session.active = true;
  session.runId = runId;
  session.lastAppliedKey = null;
  session.lastAppliedAt = Number.NEGATIVE_INFINITY;
}

function getOrCreateStreamingSession(requestId: string): StreamingSession {
  if (!streamingSessions.has(requestId)) {
    streamingSessions.set(requestId, {
      active: false,
      runId: null,
      lastAppliedKey: null,
      pendingEvent: null,
      rafHandle: null,
      lastAppliedAt: Number.NEGATIVE_INFINITY,
    });
  }

  return streamingSessions.get(requestId)!;
}

function cancelFrame(session: StreamingSession): void {
  if (session.rafHandle !== null) {
    cancelAnimationFrame(session.rafHandle);
    session.rafHandle = null;
  }
}

function cancelPendingFrame(session: StreamingSession): void {
  cancelFrame(session);
  session.pendingEvent = null;
}

export function publishStreamingToolEvent(requestId: string, event: StreamingToolEvent): void {
  if (!getAgentRunStreamingEnabled(requestId)) return;

  const session = streamingSessions.get(requestId);
  if (!session?.active || session.runId !== event.runId) {
    return;
  }

  // Final apply is authoritative and must never be dropped or deferred. Flush any
  // buffered partial first so the last preview frame is not lost (matters for
  // replaceSelection, where the streamed final is ignored), then apply the final.
  if (event.isFinal) {
    cancelFrame(session);
    if (session.pendingEvent) {
      applyStreamingToolEvent(requestId, session, session.pendingEvent);
      session.pendingEvent = null;
    }
    applyStreamingToolEvent(requestId, session, event);
    return;
  }

  // Buffer the latest partial; the flush coalesces and applies it on a frame.
  // Chunks are cumulative, so keeping only the latest is lossless preview.
  session.pendingEvent = event;
  if (session.rafHandle === null) {
    session.rafHandle = requestAnimationFrame(() => flushPendingStreamingEvent(requestId));
  }
}

function flushPendingStreamingEvent(requestId: string): void {
  const session = streamingSessions.get(requestId);
  if (!session) return;

  session.rafHandle = null;
  const event = session.pendingEvent;
  if (!event) return;

  // Cap applies at ~30fps regardless of display refresh rate (rAF fires at 60fps,
  // or 120fps on ProMotion). If the last apply was too recent, keep the buffered
  // event and reschedule for a later frame instead of applying now.
  const now = Date.now();
  if (now - session.lastAppliedAt < STREAMING_UI_FRAME_MS) {
    session.rafHandle = requestAnimationFrame(() => flushPendingStreamingEvent(requestId));
    return;
  }

  session.pendingEvent = null;

  // Re-check run scope at flush time: the run may have ended or restarted between
  // buffering and this frame. Late chunks from an older run must not mutate the editor.
  if (!session.active || session.runId !== event.runId) return;

  session.lastAppliedAt = now;
  applyStreamingToolEvent(requestId, session, event);
}

function applyStreamingToolEvent(requestId: string, session: StreamingSession, event: StreamingToolEvent): void {
  const isTargetedRun = getAgentRunEditScope(requestId) === 'targeted';
  if (getToolExecutionIgnoreReason(requestId, event.toolName)) {
    return;
  }

  if (isTargetedRun && event.toolName === 'tiptapEdit') {
    return;
  }

  if (shouldDeferTiptapEditStreamEvent(event)) {
    return;
  }

  const key = `${event.toolCallId}:${event.isFinal ? 'final' : 'partial'}:${JSON.stringify(event.input ?? null)}`;
  if (session.lastAppliedKey === key) {
    return;
  }

  const editor = getCachedEditor(requestId)?.editor as StreamableEditor | null | undefined;
  if (!isAiToolkitReady(editor)) {
    return;
  }

  session.lastAppliedKey = key;

  if (event.toolName === 'replaceSelection') {
    if (event.isFinal) {
      return;
    }

    const { status } = executeReplaceSelectionTool({
      requestId,
      toolCallId: event.toolCallId,
      editor,
      input: event.input,
      fallbackRange: getTargetedEditRange(getAgentRunEditTarget(requestId)),
    });

    // Pure telemetry observer: record only when the fragment actually rendered. 'missing-selection',
    // 'failed', and 'no-op' render nothing, so the first-content latch stays open for a later apply.
    // Only reachable with streaming enabled (publishStreamingToolEvent gates on
    // getAgentRunStreamingEnabled), and the store latches once per generation session.
    if (status === 'applied') {
      recordFirstContent(requestId);
    }
    return;
  }

  // Same pure telemetry observer as the replaceSelection branch above. Read-only tools
  // (tiptapRead etc.) also stream through this branch but render no content, so only
  // editor-write tools record.
  if (EDITOR_WRITE_TOOL_NAMES.has(event.toolName)) {
    recordFirstContent(requestId);
  }
  getAiToolkit(editor).streamTool({
    toolCallId: event.toolCallId,
    toolName: event.toolName,
    input: event.input,
    hasFinished: event.isFinal,
    reviewOptions: { mode: 'disabled' },
  });
}

function shouldDeferTiptapEditStreamEvent(event: StreamingToolEvent): boolean {
  return event.toolName === 'tiptapEdit' && hasIncompleteListHtmlInTiptapEditInput(event.input);
}

function hasIncompleteListHtmlInTiptapEditInput(input: unknown): boolean {
  const operations = isRecord(input) && Array.isArray(input.operations) ? input.operations : [];

  return operations.some((operation) => {
    const content = isRecord(operation) && typeof operation.content === 'string' ? operation.content : '';
    return hasIncompleteListHtml(content);
  });
}

function hasIncompleteListHtml(content: string): boolean {
  if (!/<\s*\/?\s*(?:ul|ol|li)(?:[\s>/]|$)/i.test(content)) {
    return false;
  }

  if (/<\s*\/?\s*(?:ul|ol|li)(?:\s[^<>]*)?$/i.test(content)) {
    return true;
  }

  const stack: string[] = [];
  const listTagPattern = /<\s*(\/?)\s*(ul|ol|li)(?:\s+[^<>]*)?>/gi;
  for (const match of content.matchAll(listTagPattern)) {
    const isClosingTag = match[1] === '/';
    const tagName = match[2].toLowerCase();

    if (!isClosingTag) {
      if (tagName === 'li' && stack.at(-1) === 'li') {
        stack.pop();
      }
      stack.push(tagName);
      continue;
    }

    if ((tagName === 'ul' || tagName === 'ol') && stack.at(-1) === 'li') {
      stack.pop();
    }

    if (stack.pop() !== tagName) {
      return true;
    }
  }

  return stack.length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function endStreamingSession(requestId: string): void {
  const session = streamingSessions.get(requestId);
  if (session) {
    cancelPendingFrame(session);
  }
  streamingSessions.delete(requestId);
}

function clearAllStreamingSessions(): void {
  streamingSessions.forEach(cancelPendingFrame);
  streamingSessions.clear();
}
